/** SUNTRONIX ONE — Sales Orders (SO). Internal-only.
 *
 * A Customer PO that passes validation is converted into a numbered
 * Sales Order that owns the fulfilment lifecycle: CONFIRMED → SHIPPED
 * → DELIVERED → INVOICED → CLOSED.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import STXReferenceNumber from "@/components/common/STXReferenceNumber";
import {
  Truck, ChevronLeft, RefreshCw, CheckCircle2, XCircle, Package, ArrowRight,
  ShoppingCart, FileText, QrCode,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const STATE_TONE = {
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  CONFIRMED: "bg-blue-100 text-blue-800 border-blue-200",
  IN_FULFILLMENT: "bg-violet-100 text-violet-800 border-violet-200",
  SHIPPED: "bg-amber-100 text-amber-800 border-amber-200",
  DELIVERED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  INVOICED: "bg-cyan-100 text-cyan-800 border-cyan-200",
  CLOSED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-rose-100 text-rose-800 border-rose-200",
};

const NEXT_STATES = {
  DRAFT: ["CONFIRMED", "CANCELLED"],
  CONFIRMED: ["IN_FULFILLMENT", "CANCELLED"],
  IN_FULFILLMENT: ["SHIPPED", "CANCELLED"],
  SHIPPED: ["DELIVERED", "CANCELLED"],
  DELIVERED: ["INVOICED"],
  INVOICED: ["CLOSED"],
  CLOSED: [],
  CANCELLED: [],
};

export default function SalesOrdersList() {
  const [rows, setRows] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const [list, dash] = await Promise.all([
        api.get(`/vtiger/sales-orders${params}`),
        api.get(`/vtiger/sales-orders/dashboard/rollup`),
      ]);
      setRows(list.data.items || []);
      setRollup(dash.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Sales Orders");
    } finally { setBusy(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const stateCounts = rollup?.by_status || {};

  return (
    <div className="space-y-2.5 max-w-[1500px]" data-testid="crm-so-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-xl font-heading font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
            <Truck className="h-5 w-5 text-emerald-700" />
            Sales Orders
          </h1>
          <div className="text-[11px] text-slate-500">
            Numbered orders generated from validated Customer POs. Native to Suntronix ONE.
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="so-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      {/* Rollup tiles */}
      {rollup && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="so-rollup">
          <RollupTile label="Total" value={rollup.total} />
          <RollupTile label="Open value" value={fmtINR(rollup.open_amount_inr)} tone="ok" />
          <RollupTile label="In fulfilment"
            value={stateCounts.IN_FULFILLMENT?.count || 0} tone="warn" />
          <RollupTile label="Shipped"
            value={stateCounts.SHIPPED?.count || 0} tone="warn" />
          <RollupTile label="Delivered"
            value={stateCounts.DELIVERED?.count || 0} tone="ok" />
        </div>
      )}

      {/* Status filter chips */}
      <div className="flex gap-1.5 flex-wrap">
        <FilterChip active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")} label={`All (${rollup?.total || 0})`} />
        {(rollup?.states || []).map((s) => (
          <FilterChip key={s} active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
            label={`${s} (${stateCounts[s]?.count || 0})`} />
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">
            No Sales Orders {statusFilter !== "all" && `with status ${statusFilter}`} yet.
            <div className="text-[11px] text-slate-400 mt-1">
              Generate one from Customer POs → Detail → Generate Sales Order.
            </div>
          </div>
        )}
        {rows.map((r) => (
          <button key={r.id} onClick={() => setSelected(r)}
            data-testid={`so-row-${r.id}`}
            className="w-full text-left p-4 flex items-center gap-4 hover:bg-slate-50">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <STXReferenceNumber value={r.code} copy asChildOfRow size={13} />
                <Badge className={"text-[10px] border " + (STATE_TONE[r.status] || "")}>
                  {r.status}
                </Badge>
              </div>
              <div className="text-[12px] text-slate-700 mt-1 truncate">
                {r.customer_name || "—"} · {r.subject}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
                {r.customer_po_code && <span>← CPO {r.customer_po_code}</span>}
                {r.quotation_code && <span>← QUO {r.quotation_code}</span>}
                {r.expected_delivery_date && (
                  <span>· ETA {new Date(r.expected_delivery_date).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500">Order value</div>
              <div className="text-[15px] font-heading font-semibold">
                {fmtINR(r.grand_total_inr)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <SODetailDrawer id={selected.id} onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function RollupTile({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded px-2.5 py-1.5 " + tint}>
      <div className="text-[9px] uppercase tracking-wider truncate">{label}</div>
      <div className="text-[14px] font-heading font-semibold truncate">{value ?? "—"}</div>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      data-testid={`so-filter-${label.replace(/\s.*/, "").toLowerCase()}`}
      className={"px-2.5 py-1 rounded text-[11px] border " + (
        active ? "border-emerald-500 bg-emerald-50 text-emerald-800"
              : "border-slate-200 text-slate-600 hover:bg-slate-50"
      )}>{label}</button>
  );
}

function SODetailDrawer({ id, onClose, onSaved }) {
  const [so, setSo] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showVPO, setShowVPO] = useState(false);
  const [einvoice, setEinvoice] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vtiger/sales-orders/${id}`);
      setSo(data);
      // Also try to load an existing e-Invoice, if any.
      try {
        const r = await api.get(`/vtiger/sales-orders/${id}/einvoice`);
        setEinvoice(r.data);
      } catch { setEinvoice(null); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); onClose(); }
    finally { setBusy(false); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const [patch, setPatch] = useState({});
  useEffect(() => {
    if (so) setPatch({
      delivery_notes: so.delivery_notes || "",
      expected_delivery_date: so.expected_delivery_date || "",
      lr_number: so.lr_number || "",
      dispatch_reference: so.dispatch_reference || "",
      tracking_url: so.tracking_url || "",
      invoice_number: so.invoice_number || "",
      invoice_date: so.invoice_date || "",
    });
  }, [so]);

  const transition = async (nextStatus) => {
    if (!window.confirm(`Transition ${so.code} to ${nextStatus}?`)) return;
    setSaving(true);
    try {
      await api.patch(`/vtiger/sales-orders/${id}`, { status: nextStatus });
      toast.success(`SO moved to ${nextStatus}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Transition failed"); }
    finally { setSaving(false); }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(patch).filter(([, v]) => v !== null && v !== ""));
      await api.patch(`/vtiger/sales-orders/${id}`, payload);
      toast.success("Sales Order updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const next = so ? (NEXT_STATES[so.status] || []) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="so-detail-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              {so?.code}
            </div>
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mt-1">
              {so?.subject || "…"}
            </h2>
            <div className="flex gap-2 mt-2">
              <Badge className={"text-[10px] border " + (STATE_TONE[so?.status] || "")}
                data-testid="so-status-badge">
                {so?.status}
              </Badge>
              {so?.customer_po_code && (
                <Badge variant="outline" className="text-[10px]">
                  ← CPO {so.customer_po_code}
                </Badge>
              )}
              {so?.quotation_code && (
                <Badge variant="outline" className="text-[10px]">
                  ← QUO {so.quotation_code}
                </Badge>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>

        {busy && <div className="mt-8 text-center text-slate-500 text-sm">Loading…</div>}
        {!busy && so && (
          <>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <InfoTile label="Customer" value={so.customer_name || "—"} />
              <InfoTile label="GSTIN" value={so.customer_gstin || "—"} />
              <InfoTile label="Order value" value={fmtINR(so.grand_total_inr)} bold />
            </div>

            {/* Transitions */}
            <div className="mt-5 border border-slate-200 rounded-md p-3 bg-slate-50/60"
              data-testid="so-transitions">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                Advance Lifecycle
              </div>
              <div className="flex gap-2 flex-wrap">
                {next.length === 0 && (
                  <span className="text-[12px] text-slate-500">Terminal state — no further transitions.</span>
                )}
                {next.map((s) => (
                  <Button key={s} size="sm" variant="outline"
                    onClick={() => transition(s)} disabled={saving}
                    data-testid={`so-transition-${s}`}
                    className={s === "CANCELLED"
                      ? "border-rose-500 text-rose-800 hover:bg-rose-50"
                      : "border-emerald-500 text-emerald-800 hover:bg-emerald-50"}>
                    {s === "CANCELLED"
                      ? <XCircle className="h-3.5 w-3.5 mr-1" />
                      : <ArrowRight className="h-3.5 w-3.5 mr-1" />}
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            {/* Editable fulfilment fields */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                <Package className="h-3 w-3" /> Fulfilment Details
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SOField label="Expected delivery">
                  <Input type="date" value={patch.expected_delivery_date || ""}
                    onChange={(e) => setPatch({ ...patch, expected_delivery_date: e.target.value })}
                    data-testid="so-eta" />
                </SOField>
                <SOField label="LR Number">
                  <Input value={patch.lr_number || ""}
                    onChange={(e) => setPatch({ ...patch, lr_number: e.target.value })}
                    data-testid="so-lr" />
                </SOField>
                <SOField label="Dispatch reference">
                  <Input value={patch.dispatch_reference || ""}
                    onChange={(e) => setPatch({ ...patch, dispatch_reference: e.target.value })}
                    data-testid="so-dispatch" />
                </SOField>
                <SOField label="Tracking URL">
                  <Input value={patch.tracking_url || ""}
                    onChange={(e) => setPatch({ ...patch, tracking_url: e.target.value })}
                    data-testid="so-tracking" />
                </SOField>
                <SOField label="Invoice number">
                  <Input value={patch.invoice_number || ""}
                    onChange={(e) => setPatch({ ...patch, invoice_number: e.target.value })}
                    data-testid="so-invoice-num" />
                </SOField>
                <SOField label="Invoice date">
                  <Input type="date" value={patch.invoice_date || ""}
                    onChange={(e) => setPatch({ ...patch, invoice_date: e.target.value })}
                    data-testid="so-invoice-date" />
                </SOField>
              </div>
              <div className="mt-3">
                <SOField label="Delivery notes">
                  <Textarea rows={2} value={patch.delivery_notes || ""}
                    onChange={(e) => setPatch({ ...patch, delivery_notes: e.target.value })}
                    data-testid="so-delivery-notes" />
                </SOField>
              </div>
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={saveDetails} disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="so-save">
                  {saving ? "Saving…" : "Save details"}
                </Button>
              </div>
            </div>

            {/* Vendor PO + e-Invoice actions */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Raise Vendor PO */}
              <div className="border border-slate-200 rounded-md p-4 bg-amber-50/40"
                data-testid="so-vpo-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <ShoppingCart className="h-3 w-3" /> Procurement — Vendor PO
                </div>
                {(so.linked_vendor_po_ids || []).length > 0 ? (
                  <div className="mt-2 text-[12px] text-slate-700">
                    <span className="font-mono font-semibold">
                      {(so.linked_vendor_po_ids || []).length}
                    </span> vendor PO(s) linked.
                    <Link to="/commercial/crm/vendor-pos" className="ml-2 text-emerald-700 hover:underline">
                      View →
                    </Link>
                  </div>
                ) : (
                  <div className="mt-1 text-[12px] text-slate-600">
                    Raise a Vendor PO to procurement now that this SO is confirmed.
                  </div>
                )}
                <div className="mt-3">
                  <Button size="sm" onClick={() => {
                      if (["DRAFT", "CANCELLED"].includes(so.status)) {
                        toast.error(`Vendor PO can be raised only after CONFIRMED — SO is ${so.status}`);
                        return;
                      }
                      setShowVPO(true);
                    }}
                    className="bg-amber-700 hover:bg-amber-800 text-white"
                    data-testid="so-raise-vpo">
                    <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Raise Vendor PO
                  </Button>
                </div>
              </div>

              {/* e-Invoice IRN */}
              <div className="border border-slate-200 rounded-md p-4 bg-cyan-50/40"
                data-testid="so-einvoice-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <QrCode className="h-3 w-3" /> GST e-Invoice — IRN
                </div>
                {einvoice ? (
                  <div className="mt-2">
                    <div className="text-[11px] text-slate-500">IRN</div>
                    <div className="text-[11px] font-mono break-all">{einvoice.irn}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-600">
                      <span>Ack# {einvoice.ack_no}</span>
                      <span>· {einvoice.ack_date}</span>
                      {einvoice.sandbox && (
                        <Badge className="text-[9px] bg-amber-100 text-amber-800 border-amber-200">
                          SANDBOX
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      {einvoice.qr_b64 && (
                        <img src={`data:image/png;base64,${einvoice.qr_b64}`}
                          alt="e-Invoice QR" className="h-24 w-24 border border-slate-200"
                          data-testid="einvoice-qr" />
                      )}
                      <div className="text-[11px] text-slate-600">
                        <div>Signed QR contains IRN + Ack + GSTIN totals.</div>
                        <a target="_blank" rel="noopener noreferrer"
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/vtiger/sales-orders/${id}/einvoice/qr`}
                          className="text-emerald-700 hover:underline">
                          Download QR PNG →
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-[12px] text-slate-600">
                    Generate a GST e-Invoice once the SO is delivered and invoice number is set.
                  </div>
                )}
                <div className="mt-3">
                  <Button size="sm" onClick={async () => {
                      if (!so.invoice_number) {
                        toast.error("Set an invoice number under Fulfilment first");
                        return;
                      }
                      if (!["DELIVERED", "INVOICED", "CLOSED"].includes(so.status)) {
                        toast.error(`IRN needs SO status DELIVERED / INVOICED / CLOSED (currently ${so.status})`);
                        return;
                      }
                      if (einvoice && !window.confirm("An IRN already exists for this SO. Generate a new one?")) return;
                      try {
                        const { data } = await api.post(
                          `/vtiger/sales-orders/${id}/generate-einvoice`, {});
                        toast.success(`IRN generated (${data.sandbox ? "sandbox" : "live"})`);
                        setEinvoice(data);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "IRN generation failed");
                      }
                    }}
                    className="bg-cyan-700 hover:bg-cyan-800 text-white"
                    data-testid="so-generate-einvoice">
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    {einvoice ? "Regenerate IRN" : "Generate e-Invoice"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Line items snapshot */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                Line items ({(so.items || []).length})
              </div>
              <div className="border border-slate-200 rounded-md divide-y">
                {(so.items || []).map((it, i) => (
                  <div key={i} className="p-2.5 flex items-center gap-3 text-[12px]">
                    <div className="flex-1">
                      <div className="font-medium">{it.product_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {it.product_code || "—"} · qty {it.quantity}
                      </div>
                    </div>
                    <div className="text-right w-24 font-mono font-semibold">
                      {fmtINR(it.line_total_inr)}
                    </div>
                  </div>
                ))}
                {(so.items || []).length === 0 && (
                  <div className="p-4 text-center text-[12px] text-slate-500">
                    No line items snapshot (source quotation had none).
                  </div>
                )}
              </div>
            </div>

            {/* Status history */}
            {(so.status_history || []).length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  Status history
                </div>
                <div className="border border-slate-200 rounded-md divide-y">
                  {so.status_history.map((h, i) => (
                    <div key={i} className="p-2 text-[11px] flex items-center gap-2">
                      <Badge className={"text-[10px] border " + (STATE_TONE[h.status] || "")}>
                        {h.status}
                      </Badge>
                      <span className="text-slate-500 font-mono">
                        {new Date(h.at).toLocaleString()}
                      </span>
                      <span className="text-slate-500">· {h.by}</span>
                      {h.note && <span className="text-slate-600 italic">— {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showVPO && so && (
        <VendorPOFromSOModal so={so}
          onClose={() => setShowVPO(false)}
          onDone={() => { setShowVPO(false); load(); }} />
      )}
    </div>
  );
}

function VendorPOFromSOModal({ so, onClose, onDone }) {
  const [vendorName, setVendorName] = useState("");
  const [vendorGstin, setVendorGstin] = useState("");
  const [taxPct, setTaxPct] = useState(18);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const items = so.items || [];
  const [costs, setCosts] = useState(() => {
    const c = {};
    items.forEach((it, i) => {
      c[i] = it.vendor_unit_cost_inr ?? 0;
    });
    return c;
  });
  const subtotal = items.reduce((s, it, i) =>
    s + (Number(it.quantity || 0) * Number(costs[i] || 0)), 0);
  const tax = subtotal * (Number(taxPct) || 0) / 100;
  const total = subtotal + tax;

  const submit = async () => {
    if (!vendorName) { toast.error("Vendor name required"); return; }
    setBusy(true);
    try {
      const payload = {
        vendor_name: vendorName,
        vendor_gstin: vendorGstin || null,
        tax_pct: Number(taxPct),
        notes: notes || null,
        cost_overrides: Object.fromEntries(
          Object.entries(costs).map(([k, v]) => [k, Number(v) || 0])),
      };
      const { data } = await api.post(
        `/vtiger/sales-orders/${so.id}/generate-vendor-po`, payload);
      toast.success(`Vendor PO ${data.code} raised · ${fmtINR(data.grand_total_inr)}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vendor PO creation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="vpo-from-so-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-md p-6 shadow-xl overflow-auto max-h-[92vh]">
        <div className="text-[10px] uppercase tracking-[0.14em] text-amber-700">
          {so.code}
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mt-1">
          Raise Vendor PO from Sales Order
        </h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <SOField label="Vendor name *">
            <Input value={vendorName} onChange={(e) => setVendorName(e.target.value)}
              data-testid="vpo-vendor-name" />
          </SOField>
          <SOField label="Vendor GSTIN">
            <Input value={vendorGstin}
              onChange={(e) => setVendorGstin(e.target.value.toUpperCase())}
              data-testid="vpo-vendor-gstin" />
          </SOField>
          <SOField label="Tax % (each line)">
            <Input type="number" value={taxPct}
              onChange={(e) => setTaxPct(e.target.value)}
              data-testid="vpo-tax-pct" />
          </SOField>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Cost per line (defaults from SO snapshot)
          </div>
          <div className="border border-slate-200 rounded divide-y">
            {items.map((it, i) => (
              <div key={i} className="p-2 flex items-center gap-2 text-[12px]"
                data-testid={`vpo-line-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.product_name}</div>
                  <div className="text-[10px] text-slate-500">
                    qty {it.quantity} · SKU {it.product_code || "—"}
                  </div>
                </div>
                <Input type="number" value={costs[i]}
                  onChange={(e) => setCosts({ ...costs, [i]: e.target.value })}
                  className="w-32 h-8"
                  data-testid={`vpo-cost-${i}`} />
              </div>
            ))}
            {items.length === 0 && (
              <div className="p-4 text-center text-[12px] text-slate-500">
                SO has no line items — cannot raise Vendor PO.
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-[12px]">
          <InfoTile label="Cost subtotal" value={fmtINR(subtotal)} />
          <InfoTile label="Tax" value={fmtINR(tax)} />
          <InfoTile label="Grand total" value={fmtINR(total)} bold />
        </div>

        <div className="mt-3">
          <SOField label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="vpo-notes" />
          </SOField>
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy || items.length === 0}
            className="flex-1 h-10 bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="vpo-submit">
            <ShoppingCart className="h-4 w-4 mr-1.5" />
            {busy ? "Creating…" : "Raise Vendor PO"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SOField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function InfoTile({ label, value, bold }) {
  return (
    <div className="border border-slate-200 rounded-md p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] mt-0.5 text-slate-900 ${bold ? "font-semibold font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}
